import React, { useState, useEffect, useCallback } from 'react';
import axiosClient from '../api/axiosClient';
import {
  PageHeader, Card, Btn, DataTable, StandardModal, Alert,
  colors, spacing, typography, radius, shadows,
  inputStyle, textareaStyle, selectStyle,
} from '../design-system';

// ── Constants ──────────────────────────────────────────────────────────────────

const STAGES = [
  { key: 'open',                 label: 'Open',                 short: '1' },
  { key: 'root_cause_analysis',  label: 'Root Cause Analysis',  short: '2' },
  { key: 'action_plan',          label: 'Action Plan',          short: '3' },
  { key: 'implementation',       label: 'Implementation',       short: '4' },
  { key: 'effectiveness_review', label: 'Effectiveness Review', short: '5' },
  { key: 'closed',               label: 'Closed',               short: '6' },
];

const SEVERITY_CONFIG = {
  minor:    { color: '#0ea5e9', bg: '#e0f2fe', label: 'Minor' },
  major:    { color: '#f59e0b', bg: '#fef3c7', label: 'Major' },
  critical: { color: '#ef4444', bg: '#fee2e2', label: 'Critical' },
};

const SOURCE_LABELS = {
  surveillance: 'Surveillance',
  audit:        'Audit',
  complaint:    'Complaint',
  internal:     'Internal',
  external:     'External',
};

const NEXT_STAGE_LABELS = {
  open:                 'Begin Root Cause Analysis',
  root_cause_analysis:  'Approve Action Plan',
  action_plan:          'Mark as Implementing',
  implementation:       'Submit for Effectiveness Review',
  effectiveness_review: 'Close CAPA',
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function SeverityBadge({ severity }) {
  const cfg = SEVERITY_CONFIG[severity] || { color: '#64748b', bg: '#f1f5f9', label: severity };
  return (
    <span style={{
      background: cfg.bg, color: cfg.color,
      border: `1px solid ${cfg.color}30`,
      borderRadius: radius.pill,
      fontSize: typography.fontSizes.xs,
      fontWeight: typography.fontWeights.bold,
      padding: '2px 8px',
      letterSpacing: '0.03em',
      textTransform: 'uppercase',
    }}>
      {cfg.label}
    </span>
  );
}

function StagePipeline({ status }) {
  const idx = STAGES.findIndex(s => s.key === status);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {STAGES.filter(s => s.key !== 'cancelled').map((stage, i) => {
        const done    = i < idx;
        const active  = i === idx;
        const future  = i > idx;
        const isLast  = i === STAGES.length - 1;
        return (
          <React.Fragment key={stage.key}>
            <div style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '11px',
              fontWeight: 700,
              background: active ? colors.primary : done ? '#dcfce7' : colors.background,
              color:      active ? '#fff' : done ? '#166534' : colors.textMuted,
              border:     `2px solid ${active ? colors.primary : done ? '#86efac' : colors.border}`,
              flexShrink: 0,
              transition: 'all 200ms',
            }}>
              {done ? '✓' : stage.short}
            </div>
            {!isLast && (
              <div style={{
                height: 2,
                width: 16,
                background: done ? '#86efac' : colors.border,
                flexShrink: 0,
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function TimelineEntry({ action }) {
  const stage = STAGES.find(s => s.key === action.stage);
  return (
    <div style={{ display: 'flex', gap: spacing.md, padding: `${spacing.sm} 0` }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: colors.primary, marginTop: 5 }} />
        <div style={{ width: 1, flex: 1, background: colors.border, marginTop: 4 }} />
      </div>
      <div style={{ flex: 1, paddingBottom: spacing.md }}>
        <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, color: colors.textMuted, letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: 2 }}>
          {stage?.label || action.stage}
        </div>
        <div style={{ fontSize: typography.fontSizes.md, color: colors.textPrimary, lineHeight: 1.5 }}>{action.description}</div>
        {action.evidence_url && (
          <a href={action.evidence_url} target="_blank" rel="noreferrer" style={{ fontSize: typography.fontSizes.xs, color: colors.primary, marginTop: 2, display: 'block' }}>
            View evidence
          </a>
        )}
        <div style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted, marginTop: 4 }}>
          {action.recorded_by} · {action.created_at ? new Date(action.created_at).toLocaleString() : ''}
        </div>
      </div>
    </div>
  );
}

// ── CAPA Detail Panel ──────────────────────────────────────────────────────────

function CAPADetail({ capa, onClose, onUpdated }) {
  const [tab,     setTab]     = useState('overview');
  const [saving,  setSaving]  = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const [showAdvance, setShowAdvance] = useState(false);
  const [advanceDesc, setAdvanceDesc] = useState('');
  const [advanceEvidence, setAdvanceEvidence] = useState('');
  const [edit, setEdit] = useState({
    root_cause:             capa.root_cause || '',
    corrective_action_plan: capa.corrective_action_plan || '',
    preventive_action_plan: capa.preventive_action_plan || '',
    effectiveness_outcome:  capa.effectiveness_outcome || '',
  });
  const [msg, setMsg] = useState(null);
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 4000); };

  const canAdvance = !['closed', 'cancelled'].includes(capa.status);
  const nextLabel  = NEXT_STAGE_LABELS[capa.status];

  const saveEdit = async () => {
    setSaving(true);
    try {
      const res = await axiosClient.patch(`/capa/${capa.id}`, edit);
      onUpdated(res.data);
      flash('Saved.');
    } catch {
      flash('Save failed.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const advance = async () => {
    if (!advanceDesc.trim()) return;
    setAdvancing(true);
    try {
      const res = await axiosClient.post(`/capa/${capa.id}/advance`, {
        description:  advanceDesc,
        evidence_url: advanceEvidence || undefined,
      });
      onUpdated(res.data);
      setShowAdvance(false);
      setAdvanceDesc('');
      setAdvanceEvidence('');
      flash(res.data.status === 'closed' ? 'CAPA closed.' : 'Stage advanced.');
    } catch {
      flash('Could not advance stage.', 'error');
    } finally {
      setAdvancing(false);
    }
  };

  const cancel = async () => {
    if (!window.confirm('Cancel this CAPA?')) return;
    try {
      const res = await axiosClient.post(`/capa/${capa.id}/cancel`);
      onUpdated(res.data);
      flash('CAPA cancelled.');
    } catch {
      flash('Failed to cancel.', 'error');
    }
  };

  const DETAIL_TABS = [
    { key: 'overview',  label: 'Overview' },
    { key: 'actions',   label: 'Analysis & Actions' },
    { key: 'timeline',  label: 'Timeline' },
  ];

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 200,
      display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
      background: 'rgba(0,0,0,0.4)',
    }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: 620,
          background: colors.surface,
          height: '100%',
          overflowY: 'auto',
          boxShadow: '-8px 0 40px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ padding: `${spacing.lg} ${spacing.lg} ${spacing.md}`, borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: spacing.sm }}>
            <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.08em', color: colors.textMuted, textTransform: 'uppercase' }}>
              {capa.reference_number}
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textMuted, fontSize: 20 }}>×</button>
          </div>
          <div style={{ fontSize: typography.fontSizes.xl, fontWeight: typography.fontWeights.bold, color: colors.textPrimary, marginBottom: spacing.sm, lineHeight: 1.2 }}>
            {capa.title}
          </div>
          <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
            <SeverityBadge severity={capa.severity} />
            <span style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>
              {SOURCE_LABELS[capa.source] || capa.source}
            </span>
            {capa.company_name && (
              <span style={{ fontSize: typography.fontSizes.xs, color: colors.textMuted }}>· {capa.company_name}</span>
            )}
          </div>
          <div style={{ marginTop: spacing.md }}>
            <StagePipeline status={capa.status} />
          </div>
        </div>

        {msg && <div style={{ padding: `${spacing.sm} ${spacing.lg}` }}><Alert type={msg.type} message={msg.text} /></div>}

        {/* Tab bar */}
        <div style={{ display: 'flex', borderBottom: `1px solid ${colors.border}`, padding: `0 ${spacing.lg}` }}>
          {DETAIL_TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: `${spacing.sm} ${spacing.md}`,
                background: 'none', border: 'none',
                borderBottom: tab === t.key ? `2px solid ${colors.primary}` : '2px solid transparent',
                marginBottom: -1,
                cursor: 'pointer',
                fontSize: typography.fontSizes.sm,
                fontWeight: tab === t.key ? typography.fontWeights.semibold : typography.fontWeights.medium,
                color: tab === t.key ? colors.primary : colors.textSecondary,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: spacing.lg, overflowY: 'auto' }}>
          {tab === 'overview' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
              <InfoGrid rows={[
                { label: 'Raised by',    value: capa.raised_by_name },
                { label: 'Assigned to',  value: capa.assigned_to_name || 'Unassigned' },
                { label: 'Source',       value: SOURCE_LABELS[capa.source] || capa.source },
                { label: 'Target date',  value: capa.target_completion_date || 'Not set' },
                { label: 'Opened',       value: capa.created_at ? new Date(capa.created_at).toLocaleDateString() : '—' },
                { label: 'Closed',       value: capa.closed_at  ? new Date(capa.closed_at).toLocaleDateString()  : '—' },
              ]} />
              <div>
                <FieldLabel>Description</FieldLabel>
                <div style={{ fontSize: typography.fontSizes.md, color: colors.textPrimary, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {capa.description}
                </div>
              </div>
            </div>
          )}

          {tab === 'actions' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.lg }}>
              <EditField label="Root Cause Analysis" field="root_cause" edit={edit} setEdit={setEdit} />
              <EditField label="Corrective Action Plan" field="corrective_action_plan" edit={edit} setEdit={setEdit} />
              <EditField label="Preventive Action Plan" field="preventive_action_plan" edit={edit} setEdit={setEdit} />
              <EditField label="Effectiveness Outcome" field="effectiveness_outcome" edit={edit} setEdit={setEdit} />
              <div>
                <Btn variant="primary" size="md" onClick={saveEdit} loading={saving}>Save</Btn>
              </div>
            </div>
          )}

          {tab === 'timeline' && (
            <div>
              {(!capa.actions || !capa.actions.length)
                ? <div style={{ color: colors.textMuted, fontSize: typography.fontSizes.sm }}>No actions recorded yet.</div>
                : capa.actions.map(a => <TimelineEntry key={a.id} action={a} />)
              }
            </div>
          )}
        </div>

        {/* Footer actions */}
        {capa.status !== 'closed' && capa.status !== 'cancelled' && (
          <div style={{ padding: spacing.lg, borderTop: `1px solid ${colors.border}`, display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
            {canAdvance && nextLabel && (
              <Btn variant="primary" size="md" onClick={() => setShowAdvance(true)}>
                {nextLabel}
              </Btn>
            )}
            <Btn variant="danger" size="md" onClick={cancel}>Cancel CAPA</Btn>
          </div>
        )}

        {/* Advance stage modal */}
        {showAdvance && (
          <StandardModal
            title={nextLabel}
            onClose={() => setShowAdvance(false)}
            width={480}
            footer={
              <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
                <Btn variant="secondary" size="md" onClick={() => setShowAdvance(false)}>Cancel</Btn>
                <Btn variant="primary" size="md" onClick={advance} loading={advancing} disabled={!advanceDesc.trim()}>
                  Confirm
                </Btn>
              </div>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              <div>
                <FieldLabel>Summary of actions taken *</FieldLabel>
                <textarea
                  style={{ ...textareaStyle, minHeight: 80 }}
                  value={advanceDesc}
                  onChange={e => setAdvanceDesc(e.target.value)}
                  placeholder="Describe what was done at this stage…"
                />
              </div>
              <div>
                <FieldLabel>Evidence URL (optional)</FieldLabel>
                <input style={inputStyle} value={advanceEvidence} onChange={e => setAdvanceEvidence(e.target.value)} placeholder="https://…" />
              </div>
            </div>
          </StandardModal>
        )}
      </div>
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: 6 }}>
      {children}
    </div>
  );
}

function EditField({ label, field, edit, setEdit }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <textarea
        style={{ ...textareaStyle, minHeight: 72 }}
        value={edit[field] || ''}
        onChange={e => setEdit(prev => ({ ...prev, [field]: e.target.value }))}
        placeholder={`Enter ${label.toLowerCase()}…`}
      />
    </div>
  );
}

function InfoGrid({ rows }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: `${spacing.sm} ${spacing.lg}` }}>
      {rows.map(r => (
        <div key={r.label}>
          <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: colors.textMuted, marginBottom: 2 }}>{r.label}</div>
          <div style={{ fontSize: typography.fontSizes.md, color: colors.textPrimary, fontWeight: typography.fontWeights.medium }}>{r.value || '—'}</div>
        </div>
      ))}
    </div>
  );
}

// ── Create CAPA Modal ──────────────────────────────────────────────────────────

function CreateCAPAModal({ onClose, onCreate }) {
  const [form, setForm] = useState({ title: '', description: '', source: 'internal', severity: 'minor', target_completion_date: '' });
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await axiosClient.post('/capa/', {
        ...form,
        target_completion_date: form.target_completion_date || undefined,
      });
      onCreate(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create CAPA.');
      setSaving(false);
    }
  };

  return (
    <StandardModal
      title="Raise New CAPA"
      onClose={onClose}
      width={540}
      footer={
        <div style={{ display: 'flex', gap: spacing.sm, justifyContent: 'flex-end' }}>
          <Btn variant="secondary" size="md" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" form="capa-create-form" variant="primary" size="md" loading={saving}>Raise CAPA</Btn>
        </div>
      }
    >
      {error && <Alert type="error" message={error} />}
      <form id="capa-create-form" onSubmit={submit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.md }}>
          <div>
            <FieldLabel>Title *</FieldLabel>
            <input style={inputStyle} value={form.title} onChange={e => set('title', e.target.value)} required />
          </div>
          <div>
            <FieldLabel>Description *</FieldLabel>
            <textarea style={{ ...textareaStyle, minHeight: 80 }} value={form.description} onChange={e => set('description', e.target.value)} required />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: spacing.md }}>
            <div>
              <FieldLabel>Source</FieldLabel>
              <select style={selectStyle} value={form.source} onChange={e => set('source', e.target.value)}>
                {Object.entries(SOURCE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <FieldLabel>Severity</FieldLabel>
              <select style={selectStyle} value={form.severity} onChange={e => set('severity', e.target.value)}>
                <option value="minor">Minor</option>
                <option value="major">Major</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          <div>
            <FieldLabel>Target Completion Date</FieldLabel>
            <input style={inputStyle} type="date" value={form.target_completion_date} onChange={e => set('target_completion_date', e.target.value)} />
          </div>
        </div>
      </form>
    </StandardModal>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

const COLS = [
  { key: 'reference_number', label: 'Reference', render: v => (
    <span style={{ fontFamily: 'monospace', fontWeight: 700, color: colors.primary, fontSize: typography.fontSizes.sm }}>{v}</span>
  )},
  { key: 'title',    label: 'Title', render: v => (
    <span style={{ fontWeight: typography.fontWeights.medium }}>{v}</span>
  )},
  { key: 'severity', label: 'Severity', render: v => <SeverityBadge severity={v} /> },
  { key: 'source',   label: 'Source',   render: v => SOURCE_LABELS[v] || v },
  { key: 'status',   label: 'Progress', render: (v, row) => <StagePipeline status={v} /> },
  { key: 'target_completion_date', label: 'Target', render: v => {
    if (!v) return <span style={{ color: colors.textMuted }}>—</span>;
    const d = new Date(v);
    const overdue = d < new Date() && true;
    return <span style={{ color: overdue ? '#ef4444' : colors.textPrimary, fontWeight: overdue ? 600 : 400 }}>{d.toLocaleDateString()}</span>;
  }},
];

const STATUS_FILTERS = [
  { value: 'all',                  label: 'All' },
  { value: 'open',                 label: 'Open' },
  { value: 'root_cause_analysis',  label: 'RCA' },
  { value: 'action_plan',          label: 'Action Plan' },
  { value: 'implementation',       label: 'Implementing' },
  { value: 'effectiveness_review', label: 'Review' },
  { value: 'closed',               label: 'Closed' },
];

export default function CAPAManagement() {
  const [capas,      setCapas]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [filter,     setFilter]     = useState('all');
  const [selected,   setSelected]   = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [msg,        setMsg]        = useState(null);
  const flash = (text, type = 'success') => { setMsg({ text, type }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await axiosClient.get(`/capa/${params}`);
      setCapas(res.data);
    } catch {
      flash('Failed to load CAPAs.', 'error');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const stats = {
    open:     capas.filter(c => !['closed','cancelled'].includes(c.status)).length,
    critical: capas.filter(c => c.severity === 'critical' && !['closed','cancelled'].includes(c.status)).length,
    overdue:  capas.filter(c => c.target_completion_date && new Date(c.target_completion_date) < new Date() && !['closed','cancelled'].includes(c.status)).length,
    closed:   capas.filter(c => c.status === 'closed').length,
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: spacing.lg }}>
      <PageHeader
        title="CAPA Management"
        subtitle="Corrective and Preventive Actions — ISO 17011 clause 8.5"
        actions={<Btn variant="primary" size="md" onClick={() => setShowCreate(true)}>Raise CAPA</Btn>}
      />

      {msg && <Alert type={msg.type} message={msg.text} />}

      {/* KPI strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: spacing.md, marginBottom: spacing.lg }}>
        {[
          { label: 'Open',     value: stats.open,     color: '#0ea5e9' },
          { label: 'Critical', value: stats.critical, color: '#ef4444' },
          { label: 'Overdue',  value: stats.overdue,  color: '#f59e0b' },
          { label: 'Closed',   value: stats.closed,   color: '#10b981' },
        ].map(k => (
          <Card key={k.label} style={{ borderTop: `3px solid ${k.color}` }}>
            <div style={{ fontSize: typography.fontSizes.xs, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: colors.textMuted }}>{k.label}</div>
            <div style={{ fontSize: '28px', fontWeight: 800, color: k.color, lineHeight: 1.1, marginTop: 4 }}>{k.value}</div>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap', marginBottom: spacing.md }}>
        {STATUS_FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setFilter(f.value)}
            style={{
              padding: '5px 14px',
              borderRadius: radius.pill,
              border: `1px solid ${filter === f.value ? colors.primary : colors.border}`,
              background: filter === f.value ? colors.primary : colors.surface,
              color: filter === f.value ? '#fff' : colors.textSecondary,
              cursor: 'pointer',
              fontSize: typography.fontSizes.sm,
              fontWeight: filter === f.value ? typography.fontWeights.semibold : typography.fontWeights.medium,
              transition: 'all 120ms',
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <Card>
        <DataTable
          columns={[
            ...COLS,
            { key: '_action', label: '', render: (_, row) => (
              <Btn variant="ghost" size="sm" onClick={() => setSelected(row)}>View</Btn>
            )},
          ]}
          rows={capas}
          keyField="id"
          loading={loading}
          emptyMessage="No CAPAs found."
        />
      </Card>

      {selected && (
        <CAPADetail
          capa={selected}
          onClose={() => setSelected(null)}
          onUpdated={updated => {
            setCapas(prev => prev.map(c => c.id === updated.id ? updated : c));
            setSelected(updated);
          }}
        />
      )}

      {showCreate && (
        <CreateCAPAModal
          onClose={() => setShowCreate(false)}
          onCreate={created => {
            setCapas(prev => [created, ...prev]);
            setShowCreate(false);
            setSelected(created);
            flash('CAPA raised.');
          }}
        />
      )}
    </div>
  );
}
